import {
  driver,
  submitFormWithValidation,
  getVisibleFormFieldNodes,
} from '@leagueplatform/masonry';

import {
  HIDDEN_CAPABILITIES_STORAGE_KEY,
  PERSONA_JUST_SELECTED_STORAGE_KEY,
  SETUP_MARKET_EXPERIENCE_FORM_ID,
} from 'common/persona-selector-signals';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';

type MarketCapability = { market: string; hiddenCapabilities: string[] };

type FieldNodeState = {
  formFieldProperties?: { formFieldId?: string };
  value?: string;
};

type FormStateReader = {
  getFormFieldNodesMap: (formId: string) => string[] | undefined;
  getNodeState: (nodeId: string) => unknown;
};

const isString = (value: unknown): value is string => typeof value === 'string';

/** Returns the market the user picked, or undefined if it can't be read. */
const readSelectedMarket = (
  stateController: FormStateReader,
): string | undefined =>
  (stateController.getFormFieldNodesMap(SETUP_MARKET_EXPERIENCE_FORM_ID) ?? [])
    .map((id) => stateController.getNodeState(id) as FieldNodeState | undefined)
    .find((state) => state?.formFieldProperties?.formFieldId === 'market')
    ?.value;

/**
 * Resolves the capability identifiers to hide for `selectedMarket`.
 *
 * Every unresolvable case — no market, no matching payload entry, a payload
 * that isn't shaped as expected — yields an empty list, so the caller hides
 * nothing rather than guessing.
 */
const resolveHiddenCapabilities = (
  payload: unknown,
  selectedMarket: string | undefined,
): string[] => {
  if (!selectedMarket) return [];

  const rawCapabilities = (payload as { marketCapabilities?: unknown } | null)
    ?.marketCapabilities;
  if (!Array.isArray(rawCapabilities)) return [];

  const match = (rawCapabilities as MarketCapability[]).find(
    (entry) => entry?.market === selectedMarket,
  );
  const hidden = match?.hiddenCapabilities;
  return Array.isArray(hidden) ? hidden.filter(isString) : [];
};

/**
 * Filters the visible field nodes for the setup-market-experience form so only
 * the persona field whose node ID corresponds to the selected market is included.
 *
 * The spec has visibility rules that should hide non-relevant persona fields on
 * web, but those rules don't fire — both persona fields remain visible regardless
 * of market selection. Without this filter:
 *   - The unselected persona field is visible + required → client-side validation
 *     fails with "Client-side validation failed".
 *   - If the user has stale selections from a prior market, both persona fields
 *     get submitted and the server returns `invalid_form_field_id`.
 *
 * Strategy: normalise the market value (e.g. "us_payer" → "uspayer") and keep
 * only the persona field node whose ID contains that normalised string. Falls
 * back to the first persona node with a non-empty value if no ID match is found.
 */
const filterSetupMarketExperienceFieldNodes = (
  nodes: ReturnType<typeof getVisibleFormFieldNodes>,
): ReturnType<typeof getVisibleFormFieldNodes> => {
  const typedNodes = nodes as Array<{
    nodeId: string;
    value?: unknown;
    formFieldProperties?: { formFieldId?: string };
  }>;

  const marketNode = typedNodes.find(
    (n) => n.formFieldProperties?.formFieldId === 'market',
  );
  const selectedMarket = marketNode?.value as string | undefined;
  if (!selectedMarket) return nodes;

  const nonPersonaNodes = typedNodes.filter(
    (n) => n.formFieldProperties?.formFieldId !== 'persona',
  );
  const personaNodes = typedNodes.filter(
    (n) => n.formFieldProperties?.formFieldId === 'persona',
  );

  // Normalise "us_payer" → "uspayer" and match against the node ID (e.g.
  // "setupMarketExperience$personaFieldUsPayer" → "setupmarketexperiencepersonafieldusPayer"
  // → contains "uspayer").
  const normalizedMarket = selectedMarket.replace(/_/g, '').toLowerCase();
  let relevantPersona = personaNodes.find((n) =>
    n.nodeId.replace(/[$_]/g, '').toLowerCase().includes(normalizedMarket),
  );

  // Fallback: first persona node that already has a value.
  if (!relevantPersona) {
    relevantPersona = personaNodes.find((n) => {
      const v = n.value;
      return Array.isArray(v) ? v.length > 0 : Boolean(v);
    });
  }

  return (
    relevantPersona ? [...nonPersonaNodes, relevantPersona] : nonPersonaNodes
  ) as ReturnType<typeof getVisibleFormFieldNodes>;
};

/**
 * Overrides the SDK's default `submitForm` handler with a wrapper that marks
 * sessionStorage on a successful setup-market-experience submit *before* the
 * on-success deeplink dispatches navigation.
 *
 * `RequireMarketAndPersona` reads that flag and skips the redirect for the
 * rest of the tab session — otherwise the guard would bounce the SE from
 * `/home` right back to `/setup-market-experience` immediately after submit
 * (MSP3-321 dropped the profile check that used to prevent this).
 *
 * The implementation mirrors the SDK's `SubmitFormActionHandler` shape (see
 * `@leagueplatform/masonry`) rather than composing with it. Composing works
 * for the current React scheduler but the ordering guarantee it relies on
 * (setItem after `await` resolves, before commit) is scheduler-implementation-
 * defined. Replicating the flow lets us insert the `setItem` between the
 * server round-trip and the deeplink dispatch unambiguously.
 */
export const initMasonryActionHandlers = () => {
  driver.actionController.overrideHandler(
    'submitForm',
    'default',
    async (action, resources) => {
      const {
        payload: {
          formId,
          formProcessorId,
          onSuccessDeepLink,
          suppressErrorModal,
        },
        namespace,
        ancestry,
        event,
      } = action;
      const { invokeAction, stateController } = resources;

      const rawFieldNodes = getVisibleFormFieldNodes(formId, stateController);
      // The setup-market-experience spec doesn't hide non-relevant persona fields
      // on web; filter them out so only the persona matching the selected market
      // is validated and submitted.
      const fieldNodes =
        formId === SETUP_MARKET_EXPERIENCE_FORM_ID
          ? filterSetupMarketExperienceFieldNodes(rawFieldNodes)
          : rawFieldNodes;

      const result = await submitFormWithValidation({
        formId,
        formProcessorId,
        fieldNodes,
        namespace,
        ancestry,
        event,
        resources,
        isBlocking: true,
        suppressErrorModal,
      });

      if (result.isSuccess && formId === SETUP_MARKET_EXPERIENCE_FORM_ID) {
        sessionStorage.setItem(PERSONA_JUST_SELECTED_STORAGE_KEY, '1');
        // The web spec does not set onSuccessDeepLink on this action; navigate
        // to home directly so the flow matches iOS / the setupMarketExperienceComplete
        // path on specs that fire that action instead.
        await invokeAction({
          type: 'deeplink',
          namespace,
          ancestry,
          event,
          payload: { url: APP_MANAGED_ROUTES.HOME },
        });
        return result;
      }

      if (result.isSuccess && onSuccessDeepLink) {
        await invokeAction({
          type: 'deeplink',
          namespace,
          ancestry,
          event,
          payload: { url: onSuccessDeepLink },
        });
      }

      return result;
    },
  );

  /**
   * Handles the `setupMarketExperienceComplete` action fired by the Masonry
   * spec after a successful market setup form submit. Persists the hidden
   * capabilities for the selected market to sessionStorage, then navigates to
   * home.
   *
   * The submit has already succeeded by the time this runs, so navigation is
   * the guarantee and capability hiding is best-effort: everything that can
   * fail (malformed payload, unreadable form state, unavailable storage) is
   * confined to the try block, and the deeplink dispatches either way. A
   * failure there means the nav shows every link, never that the user is
   * stranded on the setup page.
   *
   * Registered here rather than in the page component so the handler is
   * available before the form submission resolves, regardless of React's
   * paint/effect scheduling.
   */
  driver.actionController.overrideHandler(
    'setupMarketExperienceComplete',
    'default',
    async (action, resources) => {
      const { namespace, ancestry, event } = action;
      const { stateController, invokeAction } = resources;

      try {
        const selectedMarket = readSelectedMarket(stateController);
        sessionStorage.setItem(
          HIDDEN_CAPABILITIES_STORAGE_KEY,
          JSON.stringify(
            resolveHiddenCapabilities(action.payload, selectedMarket),
          ),
        );
      } catch {
        // The comment above only holds if nothing stale is left behind: a
        // throw skips the write, so a value from an earlier setup in this tab
        // would keep filtering the nav for the wrong market. Clear it so the
        // exception path fails open too.
        try {
          sessionStorage.removeItem(HIDDEN_CAPABILITIES_STORAGE_KEY);
        } catch {
          // Storage is unreachable, so it holds nothing to go stale.
        }
      }

      // Must be set before the deeplink so RequireMarketAndPersona lets the
      // navigation through. The submitForm override sets this flag when the spec
      // goes through submitForm, but setup-market-experience fires this action
      // directly — so we set it here too.
      try {
        sessionStorage.setItem(PERSONA_JUST_SELECTED_STORAGE_KEY, '1');
      } catch {
        // If storage is unreachable the guard will redirect; navigation still
        // dispatches so the user isn't stranded.
      }

      await invokeAction({
        type: 'deeplink',
        namespace,
        ancestry,
        event,
        payload: { url: APP_MANAGED_ROUTES.HOME },
      });

      return { isSuccess: true };
    },
  );
};
